import { Type } from '@sinclair/typebox';
import { UuidSchema } from './common.js';
import { TfTrackSchema } from './tables.js';
import { AgentAnnotationViolationSchema } from './agent-annotation.js';
// HUB-062: Create/Update body schemas were removed when POST /tracks and
// PATCH /tracks/:id were dropped in favor of the idempotent manifest upsert
// (`PUT /tracks/{id}` — see `TrackManifestSchema` in
// `./trackframe-config.ts`). The previous URL-fetch contract closed #145.
// :id path param — shared across tracks routes
export const TrackIdParamsSchema = Type.Object({
    id: UuidSchema,
});
// GET /tracks/:id/budget — returns per-track lifetime token usage + configured limit
export const BudgetResponseSchema = Type.Object({
    tokensUsed: Type.Integer(),
    tokenLimit: Type.Union([Type.Integer(), Type.Null()]),
});
// Responses
export const TrackResponseSchema = TfTrackSchema;
export const TracksListResponseSchema = Type.Array(TfTrackSchema);
// ---------------------------------------------------------------------------
// HUB-026 annotation-gate response shapes (Phase 4 — D-04 / D-16)
// ---------------------------------------------------------------------------
export const AgentAnnotationWarningSchema = Type.Object({
    code: Type.String(),
    message: Type.String(),
    operationPath: Type.Optional(Type.String()),
});
export const CreateTrackResponseSchema = Type.Intersect([
    TrackResponseSchema,
    Type.Object({
        warnings: Type.Optional(Type.Array(AgentAnnotationWarningSchema)),
    }),
]);
export const AnnotationErrorBodySchema = Type.Object({
    error: Type.Literal('invalid_agent_annotation'),
    message: Type.String(),
    operationPath: Type.Optional(Type.String()),
    field: Type.Optional(Type.String()),
    violations: Type.Array(AgentAnnotationViolationSchema),
});
export const TrackRoute422ResponseSchema = Type.Object({
    error: Type.String(),
    message: Type.String(),
    // Underlying cause for fetch failures (e.g. ECONNREFUSED, ENOTFOUND,
    // UND_ERR_CONNECT_TIMEOUT, ABORT_ERR). Populated by spec_fetch_failed.
    cause: Type.Optional(Type.String()),
    operationPath: Type.Optional(Type.String()),
    field: Type.Optional(Type.String()),
    violations: Type.Optional(Type.Array(AgentAnnotationViolationSchema)),
});
// ---- Track settings (HUB-047) ----
export const TrackSettingKeySchema = Type.String({
    pattern: '^[a-z][a-z0-9_]{0,63}$',
    minLength: 1,
    maxLength: 64,
});
/**
 * The single validation home for every writable per-track setting — both
 * `PUT /tracks/:id/settings/:key` and the agent's `hub_set_track_setting`
 * funnel through `setTrackSetting`, which looks the key up here.
 *
 * `model` / `planner_model` hold a `tf_llm_model.id` (uuid) — the HUB-082
 * registry row — NOT the old `provider:model` literal (HUB-085 D3, COP-118 D4).
 * A literal named a model but not which credential to use, so it bypassed the
 * registry row's own API key; migration `0047` hard-cuts every legacy row.
 * The two planner budget bounds (COP-118 D3) exist so an operator gets a
 * validation error at save time rather than a silently-ignored nonsense value;
 * they bound absurdity, not policy.
 */
export const TrackSettingValueSchemas = {
    token_limit: Type.Integer({ minimum: 0 }),
    model: UuidSchema,
    planner_model: UuidSchema,
    planner_wall_time_ms: Type.Integer({ minimum: 1000, maximum: 300000 }),
    planner_read_call_limit: Type.Integer({ minimum: 1, maximum: 100 }),
};
/**
 * What the hub falls back to when a track has NO row for the key — the same
 * numbers `runPlannerSubagent` applies, living here so the admin form can show
 * them as placeholders without hand-copying them (COP-118 D1).
 *
 * This is a single home, not a convenience: the numbers were duplicated between
 * `planner-subagent.ts` and the admin card, and when PLN-04 raised the planner
 * wall time from 30s to 120s the admin kept advertising `30000` for two days.
 * Nothing failed — the operator was simply told the wrong default, which is the
 * one thing a placeholder exists to get right. Only keys with a numeric
 * fallback appear; `model` / `planner_model` / `token_limit` have none.
 */
export const TrackSettingDefaults = {
    planner_wall_time_ms: 120_000,
    planner_read_call_limit: 30,
};
export const TrackSettingSchema = Type.Object({
    key: TrackSettingKeySchema,
    value: Type.Unknown(),
    updatedAt: Type.String({ format: 'date-time' }),
    updatedBy: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
});
export const TrackSettingPutBodySchema = Type.Object({
    value: Type.Unknown(),
});
export const BudgetResetResponseSchema = Type.Object({
    tokensUsed: Type.Integer({ minimum: 0 }),
});
export const TrackSettingsListResponseSchema = Type.Array(TrackSettingSchema);
export const TrackSettingParamsSchema = Type.Object({
    id: UuidSchema,
    key: TrackSettingKeySchema,
});
//# sourceMappingURL=tracks.js.map